import { createSelector } from '@ngrx/store';

import { betslipBaseSelector, betslipSelector, selectIsLinearBetslip } from '../../base/store/selectors';
import { BetslipType } from '../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../core/picks/betslip-bet-builder-pick';
import { BetBuilderPickId, PickId } from '../../core/picks/pick-id';
import { getLegsCount, isBetBuilderPickId } from '../../core/utils';
import { comboPickCountSelector, singlePickCountSelector } from '../betslip-bar/selectors';
import { betslipPicksListSelector } from '../picks/selectors';
import { CriteriaType } from '../reward-tokens/reward-tokens.model';
import { selectTokensStateContext } from '../reward-tokens/selectors';
import { getSelectedTokenAndEligibilityForContext, getSelectedTokenForContext } from '../reward-tokens/services/linear-reward-tokens.utils';
import { isFreebetToken } from '../reward-tokens/services/reward-tokens.utils';
import { settingsOddsAcceptanceSelector } from '../settings/selectors';
import { selectCurrentStake } from '../summary/selectors';
import { PickBetTrackingTypes } from '../tracking/models';
import { betslipCurrentTypeSelector } from '../types/base/selectors';
import { betslipTypeStateSelector, selectBetBuilderPicksState } from '../types/selectors';
import { GroupWithClosedLegsError } from '../validation/errors/general/group-pick-error';
import {
    selectAllBetBuilderErrors,
    selectAllCurrentBetslipErrors,
    selectBetslipTypeErrorsFactory,
    selectCurrentBetslipTypeErrors,
    selectCurrentStakeError,
    selectPickErrorsState,
} from '../validation/selectors';
import {
    isGroupPickOfferChange,
    isPickStatusClosed,
    isPickStatusLocked,
    isPickStatusOddsChangedPreCheck,
    isStakeError,
    isUnderMinimumStakeErrorPreCheck,
} from '../validation/services/utils/betslip-errors-utils';
import { QuickBetUiState } from './quick-bet.state';

export const quickBetStateSelector = createSelector(betslipSelector, (state) => state.quickBet);
export const quickBetUiStateSelector = createSelector(quickBetStateSelector, (state) => state.uiState);
export const actionButtonStateSelector = createSelector(quickBetStateSelector, (state) => state.actionButtonState);
export const betPlacedResultSelector = createSelector(quickBetStateSelector, (state) => state.betPlacedResult);
export const showQuickBetHelpSelector = createSelector(quickBetStateSelector, (state) => state.helpState);
export const selectQuickBetPickIds = createSelector(quickBetStateSelector, (state) => state.pickIds);
export const selectQuickBetStakeState = createSelector(quickBetStateSelector, (state) => state.stakeState);

export const selectLinearQuickBetBuilderState = createSelector(
    quickBetStateSelector,
    selectIsLinearBetslip,
    (state, isLinear): { isLinearQuickBetBuilder: true; pickId: PickId } | { isLinearQuickBetBuilder: false; pickId: undefined } => {
        if (state.pickIds.length === 1 && isBetBuilderPickId(state.pickIds[0]) && isLinear) {
            return {
                isLinearQuickBetBuilder: true,
                pickId: state.pickIds[0],
            };
        }

        return {
            isLinearQuickBetBuilder: false,
            pickId: undefined,
        };
    },
);

export const selectQuickBetContext = createSelector(
    betslipBaseSelector,
    selectQuickBetPickIds,
    selectIsLinearBetslip,
    (
        betslipBaseState,
        quickBetPickIds,
        isLinear,
    ): { betslipType: BetslipType.Combo } | { betslipType: BetslipType.BetBuilder | BetslipType.Single; pickId: PickId } => {
        //The betbuilder pick can come from the BB drawer OR from the QB itself
        const betBuilderPickId = quickBetPickIds.length === 1 && isBetBuilderPickId(quickBetPickIds[0]) ? quickBetPickIds[0] : null;
        if (betBuilderPickId) {
            // if it's a bet builder pick, and we're in linear mode -  all bet builder picks are stored in BetBuilderState
            if (isLinear) {
                return { betslipType: BetslipType.BetBuilder, pickId: betBuilderPickId };
            }

            // if it's a Sportcast pick and isSportcastAsCombo is false then it is stored in Single state
            if (BetBuilderPickId.isId(betBuilderPickId) && !betslipBaseState.isSportcastAsComboEnabled) {
                return { betslipType: BetslipType.Single, pickId: betBuilderPickId };
            }

            // otherwise(it's GroupPick OR Sportcast with isSportcastAsCombo==true) then we should check the Combo state
            return { betslipType: BetslipType.Combo };
        }

        // if it's a "regular" single pick, then check the Single state
        if (quickBetPickIds.length === 1) {
            return { betslipType: BetslipType.Single, pickId: quickBetPickIds[0] };
        }

        // otherwise(multiple picks), check Combo state
        return { betslipType: BetslipType.Combo };
    },
);

export const selectQuickBetRewardToken = createSelector(selectTokensStateContext, selectQuickBetContext, ({ tokens, types }, context) => {
    return getSelectedTokenForContext(context, tokens, types);
});

export const selectIsLinearQuickBetBuilder = createSelector(selectLinearQuickBetBuilderState, (state) => state.isLinearQuickBetBuilder);

export const selectAllQuickBetErrors = createSelector(
    selectIsLinearQuickBetBuilder,
    selectAllCurrentBetslipErrors,
    selectAllBetBuilderErrors,
    (isLinearQuickBetBuilder, currentBetslipErrors, betbuilderErrors) => {
        return isLinearQuickBetBuilder ? betbuilderErrors : currentBetslipErrors;
    },
);

export const selectIsQuickBetSuccess = createSelector(quickBetUiStateSelector, (state) => state === QuickBetUiState.Success);
export const selectIsQuickBetInOverlay = createSelector(quickBetStateSelector, (state) => state.inOverlay);

export const selectQuickBetTypeErrors = createSelector(
    selectIsLinearQuickBetBuilder,
    selectCurrentBetslipTypeErrors,
    selectBetslipTypeErrorsFactory(BetslipType.BetBuilder),
    (isLinearQuickBetBuilder, currentTypeErrors, betBuilderTypeErrors) => (isLinearQuickBetBuilder ? betBuilderTypeErrors : currentTypeErrors),
);

export const selectQuickBetStakeError = createSelector(
    selectIsLinearQuickBetBuilder,
    selectCurrentStakeError,
    selectAllBetBuilderErrors,
    (isLinearQuickBetBuilder, currentStakeError, betBuilderErrors) =>
        isLinearQuickBetBuilder ? betBuilderErrors.find(isStakeError) : currentStakeError,
);

export const selectQuickBetStake = createSelector(
    selectLinearQuickBetBuilderState,
    selectCurrentStake,
    selectBetBuilderPicksState,
    (state, currentStake, betBuilderPickState) =>
        state.isLinearQuickBetBuilder ? (betBuilderPickState.picks[state.pickId!.toString()]?.actualStake ?? null) : currentStake,
);

export const selectQuickBetStakeValidationState = createSelector(selectQuickBetStake, selectQuickBetStakeError, (stake, stakeError) => ({
    stake,
    stakeError,
}));

export const selectQuickBetPicksErrors = createSelector(
    selectPickErrorsState,
    betslipCurrentTypeSelector,
    selectIsLinearQuickBetBuilder,
    (pickErrors, type, isLinearQuickBetBuilder) => {
        if (isLinearQuickBetBuilder) {
            return pickErrors[BetslipType.BetBuilder];
        }

        return type ? pickErrors[type] : {};
    },
);

export const selectQuickBetErrorsState = createSelector(
    selectAllQuickBetErrors,
    selectQuickBetPicksErrors,
    quickBetStateSelector,
    (betslipErrors, pickErrors, quickBetState) => {
        const pickIds = quickBetState.pickIds;
        const quickBetPickErrors = pickIds.flatMap((pickId) => pickErrors[pickId.toString()] ?? []);
        const oddsChangedError = quickBetPickErrors.find(isPickStatusOddsChangedPreCheck);

        return {
            hasBetslipErrors: betslipErrors.length > 0,
            isLocked: quickBetPickErrors.some((error) => isPickStatusLocked(error) || isGroupPickOfferChange(error)),
            isClosed: quickBetPickErrors.some((error) => isPickStatusClosed(error) || error instanceof GroupWithClosedLegsError),
            oddsChanged: !!oddsChangedError,
            oddsAccepted: !!oddsChangedError?.userHasBeenNotified,
            hasStakeUpdateErrors: betslipErrors.some(isUnderMinimumStakeErrorPreCheck),
        };
    },
);

export const selectHasQuickBetRewardTokensErrors = createSelector(
    selectTokensStateContext,
    selectQuickBetContext,
    ({ eligibilityState, tokens, types }, rewardTokenContext) => {
        const [_, eligibility] = getSelectedTokenAndEligibilityForContext(rewardTokenContext, eligibilityState, tokens, types);

        if (!eligibility) {
            return false;
        }

        return [CriteriaType.SlipType, CriteriaType.MinimumLegs, CriteriaType.MaximumStake].some(
            // This needs to be checked against the boolean literal, as using "!" would also return "true"
            // when the criteria type is undefined. We only want to return "true" if the type is defined and false
            (criteriaType) => eligibility.softCriteriasValidity[criteriaType] === false,
        );
    },
);

export const selectQuickBetErrorsStateWithRewardsTokens = createSelector(
    selectQuickBetErrorsState,
    selectHasQuickBetRewardTokensErrors,
    (state, hasRewardTokensError) => ({
        ...state,
        hasBetslipErrors: state.hasBetslipErrors || hasRewardTokensError,
    }),
);

export const selectClosedLockedPickOverview = createSelector(
    betslipTypeStateSelector,
    betslipPicksListSelector,
    selectQuickBetPicksErrors,
    (typeState, pickList, comboPickErrors) => ({
        typeState,
        pickList,
        comboPickErrors,
    }),
);

export const selectQuickBetLockedState = createSelector(selectQuickBetErrorsState, (state) => state.isLocked);
export const selectQuickBetLockedOrClosedState = createSelector(selectQuickBetErrorsState, (state) => {
    return { isLocked: state.isLocked, isClosed: state.isClosed };
});

export const selectQuickBetActionButtonProcessing = createSelector(actionButtonStateSelector, (state) => state.isProcessing);

export const selectQuickBetPickNotificationState = createSelector(
    quickBetStateSelector,
    betslipPicksListSelector,
    settingsOddsAcceptanceSelector,
    selectQuickBetErrorsState,
    (quickBet, picksList, oddsAcceptance, errorState) => ({ quickBet, picksList, oddsAcceptance, errorState }),
);

export const selectQuickBetStakeErrorState = createSelector(selectQuickBetStakeState, selectAllQuickBetErrors, (stakeState, errors) => ({
    stakeState,
    errors,
}));

export const selectQuickBetPicks = createSelector(betslipPicksListSelector, selectQuickBetPickIds, (pickList, quickBetPickIds) => {
    const stringPickIds = quickBetPickIds.map((id) => id.toString());

    return pickList.filter((pick) => stringPickIds.includes(pick.id.toString()));
});

export const selectQuickBetBetBuilderPicks = createSelector(selectQuickBetPickIds, betslipPicksListSelector, (quickBetPickIds, pickList) => {
    const betBuilderPickIds = [...quickBetPickIds].filter(BetBuilderPickId.isId).map((id) => id.toString());

    return pickList.filter((pick) => betBuilderPickIds.includes(pick.id.toString())) as BetslipBetBuilderPick[];
});

export const selectQuickBetBetBuilderPicksCount = createSelector(selectQuickBetBetBuilderPicks, (betBuilderPicks) => betBuilderPicks.length);

export const selectQuickBetDisplayHelper = createSelector(quickBetStateSelector, (quickBetState) => quickBetState.displayHelper);

export const selectQuickBetLegsCount = createSelector(selectQuickBetPicks, (quickBetPicks) => getLegsCount(quickBetPicks));

export const selectedPickCountByCurrentType = createSelector(
    betslipCurrentTypeSelector,
    comboPickCountSelector,
    singlePickCountSelector,
    (currentType, comboCount, singleCount) => {
        if (currentType === BetslipType.Single) {
            return singleCount;
        }

        return comboCount;
    },
);

export const selectQuickBetTrackingType = createSelector(betslipBaseSelector, selectQuickBetPickIds, (betslipBaseState, quickBetPickIds) => {
    const betBuilderPickId = getBetBuilderId(quickBetPickIds);
    if (betBuilderPickId) {
        if (BetBuilderPickId.isId(betBuilderPickId) && !betslipBaseState.isSportcastAsComboEnabled) {
            return PickBetTrackingTypes.Single;
        }

        return PickBetTrackingTypes.Sgp;
    }

    if (quickBetPickIds.length === 1) {
        return isBetBuilderPickId(quickBetPickIds[0]) ? PickBetTrackingTypes.Sgp : PickBetTrackingTypes.Single;
    }

    return quickBetPickIds.some(isBetBuilderPickId) ? PickBetTrackingTypes.SgpPlus : PickBetTrackingTypes.Parlay;
});

export const selectTrackingDetails = createSelector(selectQuickBetTrackingType, selectedPickCountByCurrentType, (trackingType, pickCount) => {
    return {
        mode: trackingType,
        pickCount,
    };
});

export const selectIsQuickBetActive = createSelector(quickBetUiStateSelector, (state) => state !== QuickBetUiState.None);

export const selectQuickBetPickIdsOrBetbuilderId = createSelector(selectQuickBetPickIds, (pickIds) => {
    return [...pickIds];
});

function getBetBuilderId(quickBetPickIds: PickId[]) {
    if (quickBetPickIds.length === 1 && isBetBuilderPickId(quickBetPickIds[0])) {
        return quickBetPickIds[0];
    }

    return null;
}

export const selectQuickBetFreebetToken = createSelector(selectQuickBetRewardToken, (tokenInfo) => (isFreebetToken(tokenInfo) ? tokenInfo : null));

export const selectIsQuickBetOpen = createSelector(
    quickBetUiStateSelector,
    selectQuickBetDisplayHelper,
    (uiState, displayHelper) => uiState !== QuickBetUiState.None && !displayHelper?.hiddenByError,
);

export const selectQuickBetPicksWithErrors = createSelector(selectQuickBetPicks, selectQuickBetPicksErrors, (pickList, pickErrors) =>
    pickList.map((pick) => ({
        pick,
        errors: pickErrors[pick.id.toString()] || [],
    })),
);
export const selectQuickBetUnderMinStakeError = createSelector(
    selectAllQuickBetErrors,
    (betslipErrors) => betslipErrors.find(isUnderMinimumStakeErrorPreCheck) ?? null,
);
